import { IconButton, InputAdornment, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { MeasurementToolIcon } from '@/constants/AnnotationIcons';
import { localiseMeasurement } from '@/utils/unitSystem';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { UnitsEnum } from '@/__generated__/graphql';

type ManualHeightFieldProps = {
  manualSetHeight: string | undefined;
  handleManualHeightChange: (height: string) => void;
  handleMeasureIconClick: () => void;
  disabled?: boolean;
};

export const ManualHeightField = ({
  manualSetHeight,
  handleManualHeightChange,
  handleMeasureIconClick,
  disabled,
}: ManualHeightFieldProps) => {
  const unitSystem = useAtomValue(unitSystemState);

  const formatValue = useCallback(
    (value: string | undefined) => {
      if (!value) return '';
      return (
        localiseMeasurement({
          unitSystem,
          value: Number(value),
          sourceUnit: UnitsEnum.Meter,
          displayAsUnit: UnitsEnum.Millimeter,
          roundToDecimal: 3,
        }) ?? ''
      ).replace(/[^\d.]+$/, '');
    },
    [unitSystem]
  );

  const hasManualHeight = useMemo(() => Boolean(manualSetHeight), [manualSetHeight]);

  return (
    <TextField
      label="Manual Set Height"
      name="userManualSetHeight"
      value={formatValue(manualSetHeight)}
      helperText={hasManualHeight ? '' : 'Click a graph point to set a manual height'}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end" sx={{ gap: 0.5 }}>
              {!hasManualHeight && (
                <BlockIcon
                  fontSize="small"
                  color="disabled"
                  sx={{ cursor: 'not-allowed' }}
                  data-testid="disabled-indicator"
                />
              )}
              {hasManualHeight && (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleManualHeightChange('');
                  }}
                  disabled={disabled}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <MeasurementToolIcon
                sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                onClick={disabled ? undefined : handleMeasureIconClick}
              />
            </InputAdornment>
          ),
        },
      }}
      variant="standard"
      fullWidth
      disabled
    />
  );
};
